<script setup lang="ts">
type ToggleValue = string | number | boolean
interface ToggleOption { label: string, value: ToggleValue, disabled?: boolean }

const props = withDefaults(defineProps<{
  modelValue: ToggleValue | null
  options: Array<ToggleOption | string>
  disabled?: boolean
  name?: string
  id?: string
  ariaLabel?: string
  block?: boolean
  /**
   * When true, the control hugs its content: each option is sized to its label
   * with consistent padding (no flex stretch), so the toggle stays the same
   * regardless of having 1, 2, 3 or more options. Defaults to false to keep the
   * legacy full-width/equal-split behavior of existing consumers.
   */
  fit?: boolean
}>(), {
  disabled: false,
  block: false,
  fit: false,
})

const emit = defineEmits<{ (e: 'update:modelValue', value: ToggleValue): void }>()

const normalizedOptions = computed<ToggleOption[]>(() =>
  props.options.map(option =>
    typeof option === 'string' ? { label: option, value: option } : option,
  ),
)

function select(option: ToggleOption) {
  if (props.disabled || option.disabled || option.value === props.modelValue) {
    return
  }
  emit('update:modelValue', option.value)
}
</script>

<template>
  <div
    :id="id"
    class="segmented-toggle"
    :class="{ 'segmented-toggle--block': block, 'segmented-toggle--disabled': disabled, 'segmented-toggle--fit': fit }"
    role="radiogroup"
    :aria-label="ariaLabel"
  >
    <button
      v-for="option in normalizedOptions"
      :key="String(option.value)"
      type="button"
      role="radio"
      :name="name"
      :data-testid="`segmented-toggle-${String(option.value)}`"
      :aria-checked="option.value === modelValue"
      :disabled="disabled || option.disabled"
      class="segmented-toggle__option"
      :class="{
        'segmented-toggle__option--active': option.value === modelValue,
        'segmented-toggle__option--disabled': option.disabled,
      }"
      @click="select(option)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
.segmented-toggle {
  @apply flex w-full gap-1 p-1 rounded-[10px] bg-neutral-high-light;

  &--block {
    @apply flex w-full;
  }

  &--fit {
    @apply inline-flex w-auto;

    .segmented-toggle__option {
      @apply flex-none;
    }
  }

  &--disabled {
    .segmented-toggle__option--active {
      @apply border-neutral-high-medium;
    }

    .segmented-toggle__option:not(.segmented-toggle__option--active) {
      @apply text-neutral-low-light;
    }
  }

  &__option {
    @apply flex-1 px-4 py-2.5 rounded-[8px] border border-solid border-transparent fonts-body-medium-regular text-neutral-low-medium text-center cursor-pointer transition-all whitespace-nowrap;

    &:not(:disabled):hover {
      @apply text-neutral-low-dark;
    }

    &--active {
      @apply bg-neutral-high-pure text-neutral-low-dark fonts-body-medium-bold;

      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.08);
    }

    &:disabled {
      @apply cursor-not-allowed;
    }

    &--disabled {
      @apply opacity-50;
    }
  }
}
</style>
